


import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { BiCheckCircle } from "react-icons/bi";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import api from "../api/axios";
import { loadRazorpayScript } from "../utils/loadRazorpayScript";
import "../assets/css/PlanchangePricing.css";

function buildUpgradeBillingMessage(billing) {
    if (!billing) return null;
    const pr = Number(billing.proration_paid_inr ?? 0);
    const newP = Number(billing.new_plan_price_inr ?? 0);
    const oldP = Number(billing.old_plan_price_inr ?? 0);
    const delta = Number(billing.list_price_delta_inr ?? newP - oldP);
    const from = billing.new_subscription_charges_from
        ? new Date(billing.new_subscription_charges_from).toLocaleString()
        : null;
    return [
        `You already paid ₹${pr.toFixed(2)} for this upgrade (prorated for the rest of this billing period).`,
        "",
        `Catalog prices per period: new ₹${newP.toFixed(2)} − old ₹${oldP.toFixed(2)} = ₹${delta.toFixed(2)} (full-plan difference, not the extra charge now).`,
        from ? `Full new-plan recurring charges start from: ${from}.` : "",
        "",
        "Next: authorize future renewals in Razorpay. You should see a small verification amount — not the full new-plan price for the current period.",
    ]
        .filter((line) => line !== "")
        .join("\n");
}


const PLAN_CONTENT = {
    Starter: {
        title: "Starter Plan",
        description: "Best for beginners and casual users",
        heading: "Included:",
        features: [
            "Access to essential design elements",
            "PNG file access",
            "Unlimited downloads with coin-based access",
            "Credit top-ups available",
        ],
    },
    Premium: {
        title: "Premium Plan",
        description: "Best for creators who need more flexibility",
        heading: "Everything in Starter, plus:",
        features: [
            "Access to advanced design elements",
            "PNG file access",
            "Premium asset library access",
            "Credit top-ups available",
        ],
    },
    "Premium+": {
        title: "Premium + Plan",
        description: "Best for professionals and small teams",
        heading: "Everything in Premium, plus:",
        headerLabel: "Best Value",
        featured: true,
        features: [
            "PSD file access",
            "Access to premium editable assets",
            "Unlimited downloads with coin-based access",
            "Early access to limited-edition drops",
        ],
        addOn: "Up to 10% discount on advanced items",
    },
    Pro: {
        title: "PRO Plan",
        description: "Best for power users, teams, and businesses",
        heading: "Everything in Premium+, plus:",
        headerLabel: "Most Popular",
        popular: true,
        features: [
            "PSD file access",
            "Priority access to all premium elements",
            "Unlimited downloads with coin-based access",
            "Early access to limited-edition drops",
        ],
        addOn: "Up to 15% discount on advanced items",
    },
};

const PLAN_ORDER = ["Starter", "Premium", "Premium+", "Pro"];

const formatPrice = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "--";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(amount);
};

const Planchangeannual = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);
    const [customerSubscriptionId, setCustomerSubscriptionId] = useState(null);
    const [currentTierLevel, setCurrentTierLevel] = useState(null);
    const [dialog, setDialog] = useState({
        show: false,
        title: "",
        message: "",
        blocking: false,
    });
    const dialogResolveRef = useRef(null);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: "",
        message: "",
    });
    const confirmResolveRef = useRef(null);

    const showDialog = (title, message) => {
        dialogResolveRef.current = null;
        setDialog({
            show: true,
            title: title || "Notice",
            message: message || "",
            blocking: false,
        });
    };

    const showDialogBlocking = (title, message) =>
        new Promise((resolve) => {
            dialogResolveRef.current = resolve;
            setDialog({
                show: true,
                title: title || "Notice",
                message: message || "",
                blocking: true,
            });
        });

    const finishDialog = () => {
        setDialog((d) => ({ ...d, show: false }));
        const resolve = dialogResolveRef.current;
        dialogResolveRef.current = null;
        if (resolve) resolve();
    };

    const showConfirmDialog = (title, message) =>
        new Promise((resolve) => {
            confirmResolveRef.current = resolve;
            setConfirmDialog({
                open: true,
                title: title || "Confirm",
                message: message || "",
            });
        });

    const resolveConfirm = (yes) => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        const fn = confirmResolveRef.current;
        confirmResolveRef.current = null;
        if (fn) fn(!!yes);
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const res = await api.get("/SubscriptionRoutes/subscriptions_change", {
                params: { plan_type: "Yearly" }
            });

            setPlans(res.data.data || []);
            setCustomerSubscriptionId(res.data.customer_subscription_id ?? null);
            setCurrentTierLevel(
                res.data.current_tier_level != null ? Number(res.data.current_tier_level) : null
            );
        } catch (err) {
            console.error("Plan fetch failed", err);
        } finally {
            setLoading(false);
        }
    };


    const orderedPlans = PLAN_ORDER.map((name) => ({
        name,
        plan: plans.find(
            (item) => String(item?.sub_name || "").trim().toLowerCase() === name.toLowerCase(),
        ),
    }));

    if (loading) {
        return (
            <div className="pricing-loader" role="status" aria-live="polite">
                <div className="pricing-loader__spinner" aria-hidden="true" />
                <h5>Loading annual plans</h5>
                <p>Please wait while we prepare your available plan changes.</p>
            </div>
        );
    }

    /* ---------------- Reusable Button ---------------- */
    const renderButton = (plan, isPopular) => {
        if (!plan) return null;

        if (plan.is_current) {
            return (
                <div className={`plan-change-current ${isPopular ? "plan-change-current--popular" : ""}`}>
                    <span className="plan-change-current__dot" />
                    Current Plan
                </div>
            );
        }

        const action =
            currentTierLevel != null && plan.tier_level > currentTierLevel
                ? "upgrade"
                : currentTierLevel != null && plan.tier_level < currentTierLevel
                    ? "downgrade"
                    : "other";

        const label =
            action === "upgrade"
                ? "Upgrade Plan"
                : action === "downgrade"
                    ? "Downgrade Plan"
                    : "Change Plan";

        return (
            <Button
                label={label}
                className={[
                    "w-100",
                    "py-2",
                    "my-3",
                    "pricing-subscribe-button",
                    isPopular ? "pricing-subscribe-button--popular" : "",
                    action === "downgrade" ? "pricing-subscribe-button--downgrade" : "",
                ].filter(Boolean).join(" ")}
                loading={processingId === plan.subscription_id}
                disabled={processingId != null}
                onClick={() => handlePlanChange(plan)}
            />
        );
    };


    const openSubscriptionCheckout = async ({
        key,
        subscription_id,
        description,
        customer_order_id,
        successTitle,
        successMessage,
    }) => {
        const razorpayLoaded = await loadRazorpayScript();

        if (!razorpayLoaded) {
            showDialog("Payment gateway error", "Failed to load Razorpay checkout.");
            return;
        }

        const rzp = new window.Razorpay({
            key,
            subscription_id,
            name: "Shaadi Material",
            description,
            handler: async function (response) {
                try {
                    await api.post("/paymentrzrrts/payments/verify", {
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_subscription_id: subscription_id,
                        customer_order_id,
                    });

                    showDialog(successTitle, successMessage);
                    await fetchPlans();
                    window.location.reload();
                } catch (e) {
                    console.error(e);
                    showDialog(
                        "Verification failed",
                        e?.response?.data?.message ||
                        "Verification failed. Please refresh or contact support."
                    );
                }
            },
            modal: {
                ondismiss: () => {
                    showDialog("Payment cancelled", "You closed the Razorpay window.");
                },
            },
            theme: {
                color: "#c59d5f",
            },
        });

        rzp.open();
    };

    const openOrderCheckout = async ({
        key,
        amount,
        razorpay_order_id,
        description,
        onVerified,
    }) => {
        const razorpayLoaded = await loadRazorpayScript();

        if (!razorpayLoaded) {
            showDialog("Payment gateway error", "Failed to load Razorpay checkout.");
            return;
        }

        const rzp = new window.Razorpay({
            key,
            amount: Math.round(Number(amount) * 100),
            currency: "INR",
            order_id: razorpay_order_id,
            name: "Shaadi Material",
            description,
            handler: onVerified,
            modal: {
                ondismiss: () => {
                    showDialog("Payment cancelled", "You closed the Razorpay window.");
                },
            },
            theme: {
                color: "#c59d5f",
            },
        });

        rzp.open();
    };

    const handlePlanChange = async (plan) => {
        if (plan.is_current) return;

        if (!customerSubscriptionId) {
            showDialog(
                "No active subscription",
                "No active subscription to change. Subscribe from pricing first, or reactivate your subscription."
            );
            return;
        }

        const isUpgrade =
            currentTierLevel != null && plan.tier_level > currentTierLevel;
        const isDowngrade =
            currentTierLevel != null && plan.tier_level < currentTierLevel;

        try {
            setProcessingId(plan.subscription_id);

            if (isUpgrade) {
                const { data } = await api.post(
                    `/paymentrzrrts/subscriptions/${customerSubscriptionId}/upgrade-order`,
                    { new_subscription_id: plan.subscription_id }
                );

                if (!data?.success) {
                    showDialog("Upgrade failed", data?.message || "Could not start upgrade");
                    return;
                }

                if (data.data?.free_upgrade) {
                    const fd = data.data;

                    if (fd.needs_subscription_checkout && fd.key && fd.subscription_id) {
                        const msg = buildUpgradeBillingMessage(fd.billing);
                        if (msg) await showDialogBlocking("Upgrade billing", msg);

                        await openSubscriptionCheckout({
                            key: fd.key,
                            subscription_id: fd.subscription_id,
                            description: `Authorize — ${plan.sub_name}`,
                            customer_order_id: fd.customer_order_id,
                            successTitle: "Upgrade complete",
                            successMessage:
                                "Upgrade complete. Your new benefits apply after Razorpay confirms.",
                        });
                    } else {
                        showDialog("Upgrade complete", "Your plan has been upgraded.");
                        await fetchPlans();
                        window.location.reload();
                    }

                    return;
                }

                const { key, razorpay_order_id, amount } = data.data;

                if (data.data?.billing) {
                    const b = data.data.billing;
                    await showDialogBlocking(
                        "Pay proration",
                        `Pay ₹${Number(amount).toFixed(
                            2
                        )} now (prorated for this period).\n\nCatalog full-plan difference (new − old): ₹${Number(
                            b.list_price_delta_inr
                        ).toFixed(
                            2
                        )} per period — this payment is only the prorated share, not the full new plan price.`
                    );
                }

                await openOrderCheckout({
                    key,
                    amount,
                    razorpay_order_id,
                    description: `Upgrade — ${plan.sub_name}`,
                    onVerified: async function (response) {
                        try {
                            const verifyRes = await api.post(
                                "/paymentrzrrts/payments/verify-upgrade-order",
                                {
                                    razorpay_order_id: response.razorpay_order_id,
                                    razorpay_payment_id: response.razorpay_payment_id,
                                    razorpay_signature: response.razorpay_signature,
                                }
                            );

                            const vd = verifyRes.data;

                            if (vd?.data?.needs_subscription_checkout && vd.data.subscription_id) {
                                const m = vd.data;
                                const msg = buildUpgradeBillingMessage(m.billing);
                                if (msg) await showDialogBlocking("Upgrade billing", msg);

                                await openSubscriptionCheckout({
                                    key: m.key,
                                    subscription_id: m.subscription_id,
                                    description: `Authorize — ${plan.sub_name}`,
                                    customer_order_id: m.customer_order_id,
                                    successTitle: "Upgrade complete",
                                    successMessage:
                                        "Upgrade complete. Your new benefits apply after Razorpay confirms.",
                                });
                            } else {
                                showDialog(
                                    "Upgrade successful",
                                    "Upgrade successful. Your new benefits apply now."
                                );
                                await fetchPlans();
                                window.location.reload();
                            }
                        } catch (e) {
                            console.error(e);
                            showDialog(
                                "Verification failed",
                                e?.response?.data?.message ||
                                "Payment received but verification failed. Please refresh or contact support."
                            );
                        }
                    },
                });

                return;
            }

            if (isDowngrade) {
                const okPeriod = await showConfirmDialog(
                    "Switch at end of billing period",
                    "Your plan will switch at the end of the current billing period. Continue?"
                );
                if (!okPeriod) return;

                const okFinal = await showConfirmDialog(
                    "Confirm downgrade",
                    "Downgrades cannot be undone from this screen. Confirm downgrade?"
                );
                if (!okFinal) return;

                const { data } = await api.patch(
                    `/paymentrzrrts/subscriptions/${customerSubscriptionId}/change-plan`,
                    { new_subscription_id: plan.subscription_id }
                );

                if (!data?.success) {
                    showDialog(
                        "Downgrade failed",
                        data?.message || "Could not schedule downgrade"
                    );
                    return;
                }

                const dd = data.data;

                if (dd?.needs_subscription_checkout && dd.key && dd.subscription_id) {
                    await openSubscriptionCheckout({
                        key: dd.key,
                        subscription_id: dd.subscription_id,
                        description: `Authorize lower plan — ${plan.sub_name}`,
                        customer_order_id: dd.customer_order_id,
                        successTitle: "Downgrade authorized",
                        successMessage:
                            data.message ||
                            "Downgrade authorized. Your plan changes at the next cycle.",
                    });
                } else {
                    showDialog("Downgrade scheduled", data.message || "Downgrade scheduled.");
                    await fetchPlans();
                }

                return;
            }

            showDialog(
                "Not allowed",
                "Use the same billing interval (Yearly) as your current plan."
            );
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong";
            showDialog("Error", msg);
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <>
            <Dialog
                visible={dialog.show}
                onHide={finishDialog}
                header={dialog.title}
                modal
                closable={!dialog.blocking}
                dismissableMask={!dialog.blocking}
                draggable={false}
                resizable={false}
                style={{ width: "min(420px, 92vw)" }}
                className="plan-change-dialog"
            >
                <div className="plan-change-dialog__message">{dialog.message}</div>
                <div className="d-flex justify-content-center mt-4">
                    <Button
                        label="OK"
                        onClick={finishDialog}
                        className="pricing-subscribe-button px-4"
                    />
                </div>
            </Dialog>

            <Dialog
                visible={confirmDialog.open}
                onHide={() => resolveConfirm(false)}
                header={confirmDialog.title}
                modal
                closable={false}
                dismissableMask={false}
                draggable={false}
                resizable={false}
                style={{ width: "min(420px, 92vw)" }}
                className="plan-change-dialog"
            >
                <div className="plan-change-dialog__message">{confirmDialog.message}</div>
                <div className="d-flex justify-content-center gap-2 mt-4">
                    <Button
                        type="button"
                        label="Keep Current Plan"
                        outlined
                        className="plan-change-dialog__secondary"
                        onClick={() => resolveConfirm(false)}
                    />
                    <Button
                        type="button"
                        label="Confirm Change"
                        className="pricing-subscribe-button"
                        onClick={() => resolveConfirm(true)}
                    />
                </div>
            </Dialog>

            <Row className="justify-content-center g-4 pricing-grid plan-change-grid">
                {orderedPlans.map(({ name, plan }) => {
                    const content = PLAN_CONTENT[name];
                    const isFeatured = Boolean(content.featured);
                    const isPopular = Boolean(content.popular);

                    return (
                        <Col key={name} xs={12} md={6} lg={3} className="pricing-card">
                            <Card
                                className={[
                                    "pricing-plan-card",
                                    isFeatured ? "pricing-plan-card--featured" : "",
                                    isPopular ? "pricing-plan-card--popular greegshadow" : "",
                                    plan?.is_current ? "pricing-plan-card--current" : "",
                                ].filter(Boolean).join(" ")}
                            >
                                <Card.Header
                                    className={[
                                        "pricing-plan-card__header",
                                        isFeatured ? "pricing-plan-card__header--featured" : "",
                                        isPopular ? "pricing-plan-card__header--popular greenbg" : "",
                                    ].filter(Boolean).join(" ")}
                                >
                                    {content.headerLabel || <span aria-hidden="true">&nbsp;</span>}
                                </Card.Header>

                                <Card.Body className="pricing-plan-card__body">
                                    <div className="pricing-plan-card__intro">
                                        <h4>{content.title}</h4>
                                        <p>{content.description}</p>
                                    </div>

                                    <div className="pricing-price-box plan-change-price-box">
                                        <div className="pricing-price-box__main">
                                            <span className="pricing-price-box__currency">₹</span>
                                            <span className="pricing-price-box__current">
                                                {formatPrice(plan?.price)}
                                            </span>
                                            <span className="pricing-price-box__suffix">/-</span>
                                        </div>

                                        <div className="pricing-price-box__meta">
                                            <span>{plan?.duration_days ?? "--"} days</span>
                                            <span className="pricing-price-box__divider">•</span>
                                            <span>Billed annually</span>
                                        </div>
                                    </div>

                                    {renderButton(plan, isPopular)}

                                    <div className="pricing-features">
                                        <p className="pricing-features__heading">
                                            <strong>{content.heading}</strong>
                                        </p>

                                        <ul>
                                            <li>
                                                <BiCheckCircle />
                                                <span>
                                                    {plan?.credits_per_month ?? "--"} credits/year
                                                </span>
                                            </li>

                                            {content.features.map((feature) => (
                                                <li key={feature}>
                                                    <BiCheckCircle />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {content.addOn && (
                                            <p className="pricing-add-on">
                                                <strong>Add-ons:</strong>
                                                <span>{content.addOn}</span>
                                            </p>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
            <Row className="mx-0 pt-4">
                <Col xs={12} className="px-0">
                    <div className="annual-cancellation-note">
                        <strong>Plan changes follow your current billing cycle</strong>
                        <span>
                            Upgrades may require a prorated payment and Razorpay authorisation.
                            Downgrades are scheduled for the end of your current annual billing period.
                        </span>
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default Planchangeannual;